import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { isAdmin, can } from "./components/shared utils/auth";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SERVER_URL } from "./constants";
import { jsonOr } from "./utils/http";
import ReleaseBadge from "./ReleaseBadge";

const APP_NAME = "greaterbase";
const STORAGE_KEY = "greaterbase_tile_order";
const PROMOTED_KEY = "greaterbase_promoted_tiles";

// Tiles added to the defaults after a user may already have a saved order.
// sanitiseOrder only keeps tiles a user already has, so these would never reach
// existing users. We inject them ONCE into the saved order, then remember we've
// offered them (PROMOTED_KEY) so a deliberate removal isn't undone next load.
const PENDING_DEFAULT_TILES = ["daily_summary", "birthdays", "daily_rota", "hours", "group_gantt"];

// What guards a view: "admin" is the level, anything else names a grant.
// Kept here, beside the tiles, and read by anyone who needs to know whether a
// view may be opened — including App.js when a link asks for one directly.
export const VIEW_GATES = {
  hours: "supervisor",
  products: "finance",
  quotes: "finance",
  residents: "finance",
  outstanding: "finance",
  rent: "finance",
  agents: "admin",
  admin: "admin",
};

/** Every view a link may ask for, so a hand-typed one lands somewhere real. */
export const VIEW_KEYS = [
  "landing",
  "students",
  "adults",
  "groups",
  "group_gantt",
  "timetable",
  "transport",
  "bedrooms",
  "classes",
  "transfers",
  "activities",
  "daily_rota",
  "daily_summary",
  "birthdays",
  "staff_schedule",
  "hours",
  "products",
  "quotes",
  "residents",
  "outstanding",
  "rent",
  "allergies",
  "agents",
  "admin",
  // Not a tile: the Help button in the top bar.
  "help",
];

/** The address of a view: what a tile links to, and what a new tab opens. */
export function viewHref(key) {
  return `?view=${encodeURIComponent(key)}`;
}

/** May this account open that view at all? Unknown views are refused. */
export function canOpenView(key) {
  if (!VIEW_KEYS.includes(key)) return false;
  const gate = VIEW_GATES[key];
  if (!gate) return true;
  return gate === "admin" ? isAdmin() : can(gate);
}

/**
 * A plain left click is the app navigating; anything else is the browser
 * being asked to do something with a link — open it in a tab, a window, or
 * save it — and should be left alone.
 */
export function isPlainClick(event) {
  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);
}

const DEFAULT_ORDER = [
  "students",
  "adults",
  "groups",
  "group_gantt",
  "timetable",
  "transport",
  "bedrooms",
  "classes",
  "transfers",
  "activities",
  "daily_rota",
  "daily_summary",
  "birthdays",
  "staff_schedule",
  "hours",
  "products",
  "quotes",
  "residents",
  "outstanding",
  "rent",
  "allergies",
  "agents",
  "admin",
];

const NON_REMOVABLE = new Set(["students", "adults", "groups", "timetable"]);

function sanitiseOrder(arr) {
  const known = new Set(DEFAULT_ORDER);
  const seen = new Set();
  const cleaned = [];
  for (const k of arr) {
    if (known.has(k) && !seen.has(k)) {
      cleaned.push(k);
      seen.add(k);
    }
  }
  for (const k of NON_REMOVABLE) {
    if (!seen.has(k)) cleaned.push(k);
  }
  return cleaned;
}

function loadCachedOrder() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [...DEFAULT_ORDER];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [...DEFAULT_ORDER];
    return sanitiseOrder(parsed);
  } catch {
    return [...DEFAULT_ORDER];
  }
}

function cacheOrder(order) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
}

function fetchOrder() {
  const token = sessionStorage.getItem("bearer");
  return fetch(`${SERVER_URL}api/my-preferences/${APP_NAME}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => (jsonOr(res, null)))
    .then((data) => {
      if (data?.tileOrder && Array.isArray(data.tileOrder)) {
        const valid = sanitiseOrder(data.tileOrder);
        return valid.length > 0 ? valid : null;
      }
      return null;
    })
    .catch(() => null);
}

function loadPromotedTiles() {
  try {
    const stored = JSON.parse(localStorage.getItem(PROMOTED_KEY));
    return new Set(Array.isArray(stored) ? stored : []);
  } catch {
    return new Set();
  }
}

function markTilesPromoted(keys) {
  if (!keys.length) return;
  const set = loadPromotedTiles();
  keys.forEach((k) => set.add(k));
  localStorage.setItem(PROMOTED_KEY, JSON.stringify([...set]));
}

// Append any pending default tile that's missing AND hasn't been offered before.
// Appends (rather than reordering) so a user's custom arrangement is preserved.
function promotePendingDefaults(order) {
  const promoted = loadPromotedTiles();
  const present = new Set(order);
  const toAdd = PENDING_DEFAULT_TILES.filter((k) => !present.has(k) && !promoted.has(k));
  return { order: toAdd.length ? [...order, ...toAdd] : order, added: toAdd };
}

function saveOrder(order) {
  cacheOrder(order);
  const token = sessionStorage.getItem("bearer");
  return fetch(`${SERVER_URL}api/my-preferences/${APP_NAME}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tileOrder: order }),
  }).catch(() => {});
}

function SortableTile({ tile, tileMinHeight, iconSize, fontSize, onSelect, reordering, canRemove, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ width: "100%" }}>
      <Card
        elevation={2}
        sx={{
          minHeight: tileMinHeight,
          borderRadius: 3,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          position: "relative",
        }}
      >
        {reordering && canRemove && (
          <Box
            onClick={(e) => { e.stopPropagation(); onRemove(tile.key); }}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "error.main",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              "&:hover": { bgcolor: "error.dark" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </Box>
        )}
        {reordering && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1,
              cursor: "grab",
              touchAction: "none",
              color: "grey.400",
              "&:hover": { color: "grey.600" },
            }}
          >
            <DragIndicatorIcon />
          </Box>
        )}
        <CardActionArea
          component="a"
          href={viewHref(tile.key)}
          onClick={(e) => {
            // Ctrl/⌘-click, middle click and the context menu are the
            // browser's: let the link be a link. A plain click is us.
            if (!isPlainClick(e)) return;
            e.preventDefault();
            onSelect(tile.key);
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: tileMinHeight,
            p: 2,
            flex: 1,
          }}
        >
          <Box
            component="img"
            src={tile.src}
            alt={tile.label}
            sx={{
              width: iconSize,
              height: iconSize,
              objectFit: "contain",
              mb: 1,
            }}
          />
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Typography
              align="center"
              sx={{ fontSize, fontWeight: 700, lineHeight: 1.2 }}
            >
              {tile.label}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
}

export default function LandingPage({ onSelect }) {
  const publicUrl = process.env.PUBLIC_URL || "";
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const tileMinHeight = isMobile ? 90 : isTablet ? 120 : 160;
  const iconSize = isMobile ? 32 : isTablet ? 48 : 64;
  const fontSize = isMobile ? "0.75rem" : isTablet ? "0.9rem" : "1rem";

  const tileMap = useMemo(
    () => ({
      students: { label: "Students", src: `${publicUrl}/assets/img/students.png`, key: "students" },
      adults: { label: "Adults", src: `${publicUrl}/assets/img/adults.png`, key: "adults" },
      timetable: { label: "Timetable", src: `${publicUrl}/assets/img/timetable.png`, key: "timetable" },
      groups: { label: "Groups", src: `${publicUrl}/assets/img/groups.png`, key: "groups" },
      group_gantt: { label: "GANTT", src: `${publicUrl}/assets/img/planning_5434653.png`, key: "group_gantt" },
      transport: { label: "Transport", src: `${publicUrl}/assets/img/transport.png`, key: "transport" },
      bedrooms: { label: "Bedrooms", src: `${publicUrl}/assets/img/dorms.png`, key: "bedrooms" },
      classes: { label: "Class Lists", src: `${publicUrl}/assets/img/Classroom.png`, key: "classes" },
      transfers: { label: "Transfers", src: `${publicUrl}/assets/img/transfers.png`, key: "transfers" },
      activities: { label: "Activity Plan", src: `${publicUrl}/assets/img/activities.png`, key: "activities" },
      daily_rota: { label: "Daily Rota", src: `${publicUrl}/assets/img/daily-routine_14991730.png`, key: "daily_rota" },
      daily_summary: { label: "Daily Summary", src: `${publicUrl}/assets/img/playbook.png`, key: "daily_summary" },
      birthdays: { label: "Birthdays", src: `${publicUrl}/assets/img/birthdays.png`, key: "birthdays" },
      staff_schedule: { label: "Staff Schedule", src: `${publicUrl}/assets/img/staff_schedule.png`, key: "staff_schedule" },
      hours: { label: "Hours & Overtime", src: `${publicUrl}/assets/img/clipboard_4459443.png`, key: "hours" },
      products: { label: "Products", src: `${publicUrl}/assets/img/products.png`, key: "products" },
      quotes: { label: "Quotes", src: `${publicUrl}/assets/img/quote.png`, key: "quotes" },
      residents: { label: "Residents", src: `${publicUrl}/assets/img/bedroom.png`, key: "residents" },
      outstanding: { label: "Outstanding Balances", src: `${publicUrl}/assets/img/bill.png`, key: "outstanding" },
      rent: { label: "Rent", src: `${publicUrl}/assets/img/rent.png`, key: "rent" },
      allergies: { label: "Allergies", src: `${publicUrl}/assets/img/allergies.png`, key: "allergies" },
      agents: { label: "Agents", src: `${publicUrl}/assets/img/agent.png`, key: "agents" },
      admin: { label: "Admin", src: `${publicUrl}/assets/img/admin.png`, key: "admin" },
    }),
    [publicUrl]
  );

  const [order, setOrder] = useState(loadCachedOrder);
  const [reordering, setReordering] = useState(false);
  const longPressTimer = useRef(null);

  useEffect(() => {
    fetchOrder().then((remote) => {
      const base = remote || loadCachedOrder();
      const { order: next, added } = promotePendingDefaults(base);
      // Tiles already in the stored order have plainly been offered, so a
      // later removal sticks.
      markTilesPromoted(PENDING_DEFAULT_TILES.filter((k) => base.includes(k)));
      if (remote || added.length) {
        setOrder(next);
        cacheOrder(next);
      }
      // Persist an injection so it's permanent and reaches the user's other
      // devices — and only call it offered once that has landed. Marking it
      // first lost the tile for good if a second mount (React's double effect
      // in dev, or a quick re-navigation) re-read the older remote order
      // before the save arrived: "already offered" with nothing to show for it.
      if (added.length) {
        saveOrder(next)
          .then(() => markTilesPromoted(added))
          .catch(() => {});
      }
    });
  }, []);

  const handleTilePointerDown = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      setReordering(true);
    }, 600);
  }, []);

  const handleTilePointerUp = useCallback(() => {
    clearTimeout(longPressTimer.current);
  }, []);

  const canSeeTile = useCallback(
    (key) => {
      if (!tileMap[key]) return false;
      return canOpenView(key);
    },
    [tileMap]
  );

  const visibleOrder = useMemo(
    () => order.filter(canSeeTile),
    [order, canSeeTile]
  );

  const tiles = useMemo(
    () => visibleOrder.map((key) => tileMap[key]).filter(Boolean),
    [visibleOrder, tileMap]
  );

  const hiddenTiles = useMemo(
    () =>
      DEFAULT_ORDER
        .filter((k) => !order.includes(k) && canSeeTile(k))
        .map((k) => tileMap[k])
        .filter(Boolean),
    [order, tileMap, canSeeTile]
  );

  const handleRemoveTile = useCallback((key) => {
    if (NON_REMOVABLE.has(key)) return;
    setOrder((prev) => {
      const next = prev.filter((k) => k !== key);
      saveOrder(next);
      return next;
    });
  }, []);

  const handleAddTile = useCallback((key) => {
    setOrder((prev) => {
      if (prev.includes(key)) return prev;
      const next = [...prev, key];
      saveOrder(next);
      return next;
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newOrder = arrayMove(order, oldIndex, newIndex);
    setOrder(newOrder);
    saveOrder(newOrder);
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        pt: 2,
        pb: 4,
        minHeight: "100vh",
        boxSizing: "border-box",
        alignSelf: "stretch",
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={visibleOrder} strategy={rectSortingStrategy}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(4, 1fr)",
              },
              gap: isMobile ? 1.5 : 2,
            }}
          >
            {tiles.map((tile) => (
              <Box
                key={tile.key}
                onPointerDown={handleTilePointerDown}
                onPointerUp={handleTilePointerUp}
                onPointerLeave={handleTilePointerUp}
              >
                <SortableTile
                  tile={tile}
                  tileMinHeight={tileMinHeight}
                  iconSize={iconSize}
                  fontSize={fontSize}
                  onSelect={reordering ? () => {} : onSelect}
                  reordering={reordering}
                  canRemove={!NON_REMOVABLE.has(tile.key)}
                  onRemove={handleRemoveTile}
                />
              </Box>
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      {reordering && hiddenTiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
            Hidden tiles
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {hiddenTiles.map((tile) => (
              <Card
                key={tile.key}
                sx={{
                  minHeight: 80,
                  minWidth: 140,
                  flex: "0 1 160px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  opacity: 0.6,
                  position: "relative",
                }}
              >
                <Box
                  onClick={() => handleAddTile(tile.key)}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    zIndex: 2,
                    "&:hover": { bgcolor: "success.dark" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    p: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={tile.src}
                    alt={tile.label}
                    sx={{ width: 40, height: 40, objectFit: "contain", mb: 0.5 }}
                  />
                  <Typography variant="body2" align="center" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                    {tile.label}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {reordering && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button variant="contained" onClick={() => setReordering(false)}>
            Done
          </Button>
        </Box>
      )}

      <ReleaseBadge />
    </Box>
  );
}
